import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Zap, ArrowRight, Clock, Scale, Briefcase, Bot } from "lucide-react";
import { BlogNav, BlogFooter } from "./BlogComponents";

const blogPosts = [
  {
    slug: "best-ai-intake-software-for-law-firms-2026",
    title: "Best AI Intake Software for Law Firms (2026)",
    description: "Compare the top AI-powered intake tools for law firms. Learn how AI scoring helps qualify cases faster and reduce unqualified consultations.",
    icon: <Scale className="w-6 h-6" />,
    category: "Law Firms",
    readTime: 8,
    date: "March 1, 2026"
  },
  {
    slug: "top-lead-qualification-tools-for-service-businesses",
    title: "Top Lead Qualification Tools for Service Businesses",
    description: "Discover the best tools to qualify leads automatically. From AI scoring to CRM pipelines, find the right fit for your service business.",
    icon: <Briefcase className="w-6 h-6" />,
    category: "Service Businesses",
    readTime: 10,
    date: "March 1, 2026"
  },
  {
    slug: "how-to-automate-client-screening-with-ai",
    title: "How to Automate Client Screening with AI",
    description: "Step-by-step guide to automating your client screening process. Includes sample questions, scoring criteria, and outcome templates.",
    icon: <Bot className="w-6 h-6" />,
    category: "How-To Guide",
    readTime: 12,
    date: "March 1, 2026"
  }
];

export default function BlogIndexPage() {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 }
  };

  return (
    <>
      <Helmet>
        <title>Blog | HeyIntake - AI Intake & Lead Scoring Insights</title>
        <meta name="description" content="Learn how to qualify leads faster with AI intake software. Guides, comparisons, and best practices for service businesses and law firms." />
        <link rel="canonical" href="https://heyintake.com/blog" />
      </Helmet>

      <div className="min-h-screen bg-[#f5f5f0]">
        <BlogNav />

        {/* Hero */}
        <section className="py-16 md:py-20 px-4 bg-white">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div {...fadeIn}>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                HeyIntake Blog
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                Practical guides on AI-powered lead qualification, client intake automation, and growing your service business.
              </p>
              <div className="flex justify-center gap-4">
                <Link to="/register">
                  <Button>Start 7-day free trial <ArrowRight className="ml-2 w-4 h-4" /></Button>
                </Link>
                <Link to="/demo">
                  <Button variant="outline">Book a demo</Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="py-16 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {blogPosts.map((post, i) => (
                <motion.div
                  key={post.slug}
                  {...fadeIn}
                  transition={{ delay: i * 0.1 }}
                >
                  <Link to={`/blog/${post.slug}`}>
                    <Card className="h-full hover:shadow-lg transition-shadow bg-white">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary">
                            {post.icon}
                          </div>
                          <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
                            {post.category}
                          </span>
                        </div>
                        <h2 className="text-lg font-bold text-foreground mb-2 line-clamp-2">
                          {post.title}
                        </h2>
                        <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                          {post.description}
                        </p>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{post.date}</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {post.readTime} min read
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-primary text-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to qualify leads with AI?</h2>
            <p className="text-white/80 mb-8">
              HeyIntake scores every submission automatically, so you know who deserves your time.
            </p>
            <div className="flex justify-center gap-4 flex-wrap">
              <Link to="/register">
                <Button variant="secondary" size="lg">
                  Start 7-day free trial <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/compare/heyintake-vs-intakeq-vs-jotform">
                <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10">
                  Compare options
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <BlogFooter />
      </div>
    </>
  );
}
